/**
 * GoalsLedger — the one-line goals home, built to MATCH the live tasks queue
 * (`/work?area=queue` = TasksHeader + GroupedTaskList variant="flat"). Every goal
 * is ONE scannable row: a goal-tile SIGNIFIER (Target = strategic, Bot = agentic)
 * where an issue carries a bare glyph, a state dot, the title, and a trailing
 * MILESTONE track (an issue ends in a due date; a goal ends in its milestone
 * track). Everything a goal *contains* — outcome, key results, the fleet, momentum
 * — lives on the goal page, one click away.
 *
 * Two modes, picked automatically from the data:
 *   • CONSOLIDATED (the /work/goals + /agents/goals home) — when the list carries
 *     BOTH kinds: a kind filter (All · Strategic · Agentic) caps rows GROUPED into
 *     Strategic + Agentic sections. Strategic rows read their dot from the OKR
 *     band; agentic rows from task health. Its controls (kind · lifecycle · the
 *     injected create CTA) ride the page TITLE row as one cluster.
 *   • FLAT (a lane's embedded list, agentic-only callers) — the original single
 *     HEALTH-filter bar over a flat agentic list. Unchanged.
 *
 * Graduated from /styleguide/goals. Pure client — the segment filter is local
 * state; the page owns the data.
 */

import * as React from "react";
import { RouterLink as Link } from "../../router";
import { objectHref } from "../../path-label";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { cn } from "../../utils";
import { roleLabel } from "../../format";
import { INTENT_CLASSES, sectionColor, type Intent } from "../../tokens";
import {
  Activity,
  Bot,
  ChevronDown,
  CircleDashed,
  Filter,
  Kanban,
  List,
  Plus,
  Target,
  User,
} from "../../icons";
import { EmptyState } from "../empty-state";
import { WorkRow, type WorkRowModel, type WorkState } from "../work-row";
import { SectionHeader } from "../list-card";
import { SectionActions } from "../section-actions";
import { ShellActionsPortal } from "../../shell-title-context";
import { FilterBar } from "../filter-bar";
import { DisplayMenu, FilterMenu, type FilterField } from "../view-menu";
import type { GoalStatus } from "../../tokens";
import { goalHealth, GOAL_HEALTH_META, type GoalHealth } from "./goal-health";
import { GOAL_KIND_META } from "./goal-kind";
import { goalTrack } from "./goal-track";
import type { GoalFinishLine } from "./goal-definition";
import type { GoalMilestone } from "./goal-milestones";

/** The OKR "on plan?" verdict for a strategic goal (mirrors the objectives band). */
export type GoalBand = "ok" | "watch" | "risk" | "pending";

const BAND_INTENT: Record<GoalBand, Intent> = {
  ok: "success",
  watch: "warning",
  risk: "danger",
  pending: "neutral",
};
const BAND_LABEL: Record<GoalBand, string> = {
  ok: "on plan",
  watch: "watch",
  risk: "at risk",
  pending: "pending",
};

export interface GoalLedgerItem {
  id: number;
  uid: string | null;
  /** Strategic (human-owned, OKR) vs agentic (fleet-pursued). Drives the row's
   *  signifier + which state signal (band vs task-health) sets its dot. */
  kind: "agentic" | "strategic";
  /** The outcome headline (the title in the new grammar). */
  title: string;
  /** The one-sentence result statement; null ⇒ a thin/undefined goal. */
  outcome: string | null;
  status: GoalStatus;
  /** Rollup for the health verdict (agentic). */
  task_count: number;
  done_task_count: number;
  cancelled_task_count: number;
  /** The compact finish line (metric / criteria / none). */
  finishLine: GoalFinishLine;
  /** The goal's named checkpoints (v135). When shaped, the row's trailing signal
   *  is the milestone track; when empty, it falls back to the finish line (agentic)
   *  or the band label (strategic). */
  milestones: GoalMilestone[];
  /** Strategic only — the OKR band, rolled from key-result attainment. */
  band?: GoalBand;
  /** Strategic only — how many key results are defined. The row's progress
   *  track is built from them (the server folds them into `finishLine`); this
   *  count is what surfaces outside the ledger read to prompt for a first KR. */
  krCount?: number;
  /** Trailing chip — a Lane name when present, else the driving role. */
  lane: string | null;
  role: string;
}

interface GoalsLedgerProps {
  goals: GoalLedgerItem[];
  /** Base path goal links resolve against (e.g. `/agents`). */
  basePath: string;
  /** The header toolbar (Reconcile + New goal), injected by the page. */
  toolbar?: React.ReactNode;
}

// ── The row's state dot: band for strategic, task-health for agentic ──────────

function rowDot(item: GoalLedgerItem): { intent: Intent; label: string } {
  // A strategic goal reads its state from the OKR band — but ONLY once it has a
  // measurable key result. Without one the band is permanently "pending", which
  // pinned every strategic row to a gray dot while agentic rows showed real
  // health. Both kinds fall through to the same task-health verdict, so an
  // unmeasured goal of either kind reports the same state for the same reason.
  if (item.kind === "strategic" && item.band != null && item.band !== "pending") {
    return { intent: BAND_INTENT[item.band], label: BAND_LABEL[item.band] };
  }
  const h = goalHealth(item);
  return { intent: GOAL_HEALTH_META[h].intent, label: GOAL_HEALTH_META[h].label };
}

// ── Shared one-line row (both kinds, so the columns line up) ──────────────────
// Exported so summary surfaces (the Work dashboard's Goals section) render the
// SAME row the goals home does — one grammar, no parallel card design.

function goalStatusPhase(status: GoalStatus): WorkState {
  switch (status) {
    case "done": return { as: "phase", phase: "done" };
    case "abandoned": return { as: "phase", phase: "cancelled" };
    case "paused": return { as: "phase", phase: "active", label: "Paused" };
    default: return { as: "phase", phase: "active" };
  }
}

/** GoalLedgerItem → the canonical WorkRow. The tinted GoalKindTile becomes a
 *  bare glyph SIGNIFIER (WorkRow retired the tile); the goal's LIFECYCLE (status)
 *  is the state ring; its HEALTH (rowDot's band/task verdict) is the attention
 *  MARK — the row system's separation of "where it is" from "is anyone needed",
 *  which the old single health-dot conflated. The trailing track is the meter,
 *  the lane/role is the one pill, and an object uid opens the peek in place. */
export function goalToWorkRow(item: GoalLedgerItem, href: string): WorkRowModel {
  const dot = rowDot(item);
  const track = goalTrack(item);
  const chip = item.lane ?? item.role;
  const meta = GOAL_KIND_META[item.kind];
  const level: "warning" | "danger" | null =
    dot.intent === "danger" ? "danger" : dot.intent === "warning" ? "warning" : null;
  return {
    id: item.id,
    signifier: { as: "glyph", icon: meta.Glyph, label: `${meta.label} goal` },
    state: goalStatusPhase(item.status),
    title: item.title,
    // health → the mark; a healthy goal (ok/on-plan) shows none.
    attention: level ? { label: dot.label, level } : undefined,
    // A goal with no outcome stated is still a goal; the muted note carries it.
    subtitle: item.kind === "agentic" && item.outcome === null ? "no outcome" : undefined,
    meter: { value: track.value, max: track.max, caption: track.caption },
    pills: [
      {
        icon: item.kind === "strategic" ? User : Bot,
        label: item.lane ?? roleLabel(item.role),
        hint: item.lane ? "Lane" : "Role",
        accent: sectionColor(chip),
      },
    ],
    href: item.uid ? objectHref(item.uid) : href,
  };
}

export function GoalLedgerRow({
  item,
  href,
  flush = false,
}: {
  item: GoalLedgerItem;
  /**
   * Fallback destination — the `/agents/goals/<id>` route. A goal carrying its
   * synced `uid` prefers the canonical object href instead, which makes the row
   * open the goal's PEEK in place (Matt, 2026-07-20: "when I click on a goal it
   * should show the modal first"). The id route only redirects there anyway, so
   * the peek is strictly better: same destination, no round trip.
   */
  href: string;
  /** Flush on the grid line (no horizontal inset) — for a bare dashboard slice
   *  sitting directly on the page background under a Section eyebrow, matching
   *  `LifecycleRow`. Card-wrapped ledgers keep the px-4. */
  flush?: boolean;
}) {
  return <WorkRow row={goalToWorkRow(item, href)} gutter={!flush} />;
}

// ── Consolidated mode (both kinds) — kind filter + grouped sections ───────────


/** Lifecycle slice. A goals home is a list of COMMITMENTS you can still act on,
 *  so `open` is the default — the consolidated ledger used to render every goal
 *  ever created (17 of 22 agentic goals were already `done`), which read as an
 *  inert archive with no actionable row in sight. Finished goals stay one
 *  dropdown away; they are history, not work. Mirrors FlatLedger, whose "All"
 *  segment has always meant `status === 'active'`. */
type StatusSeg = "open" | "settled" | "all";

const STATUS_LABEL: Record<StatusSeg, string> = {
  open: "Open",
  settled: "Settled",
  all: "All goals",
};

/** Open = still steering (active or paused). Settled = done or abandoned. */
function inStatusSeg(item: GoalLedgerItem, seg: StatusSeg): boolean {
  if (seg === "all") return true;
  const settled = item.status === "done" || item.status === "abandoned";
  return seg === "settled" ? settled : !settled;
}

/**
 * The imperative "open the create-goal modal" bus. The ledger lives in
 * `packages/ui` and the modal (`NewGoalDialog`) lives in the app, and half the
 * ledger's callers are SERVER components — so a callback prop isn't available.
 * The `+` dispatches, the mounted dialog listens, pre-set to the kind of the
 * section you clicked. Mirrors `excellent:open-chat` / `excellent:ask-agent`.
 */
export function requestNewGoal(kind: "agentic" | "strategic") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("excellent:new-goal", { detail: { kind } }));
}

/**
 * One kind's card — the disclosure header + its rows. Both sections render
 * through this so the grammar can't drift:
 *   • the leading glyph is a CHEVRON that folds the list (the Target/Bot marks
 *     already ride every row's signifier tile — a second static copy in the
 *     header spent the slot on decoration instead of control);
 *   • the subtitle hangs on the TITLE's left edge, not the card gutter;
 *   • a `+` sits on the title line and opens the create modal on this kind.
 */
function LedgerSection({
  kindLabel,
  subtitle,
  count,
  canCreate,
  createKind,
  children,
}: {
  kindLabel: string;
  subtitle: string;
  count: number;
  canCreate: boolean;
  createKind: "agentic" | "strategic";
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(true);
  const bodyId = React.useId();
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="p-0">
        <SectionHeader
          title={
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={bodyId}
              className="-my-1 flex items-center gap-2 rounded-sm py-1 text-left transition-colors hover:text-muted-foreground"
            >
              <span className="inline-flex w-4 items-center justify-center" aria-hidden>
                <ChevronDown
                  className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
                />
              </span>
              {kindLabel}
            </button>
          }
          // pl-6 = the w-4 chevron + the gap-2 beside it, so the description
          // starts on the same vertical line as the section title.
          subtitle={<span className="block pl-6">{subtitle}</span>}
          count={count}
          action={
            canCreate ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`New ${createKind} goal`}
                title={`New ${createKind} goal`}
                onClick={() => requestNewGoal(createKind)}
              >
                <Plus className="size-3.5" />
              </Button>
            ) : undefined
          }
        />
        {open ? <div id={bodyId}>{children}</div> : null}
      </CardContent>
    </Card>
  );
}

function ConsolidatedLedger({ goals, basePath, toolbar }: GoalsLedgerProps) {
  // Kind is now a MULTI filter (both / either) rather than a three-way segment —
  // the checkbox submenu expresses "strategic and agentic" natively, and an empty
  // set means no constraint, which is what the old "All kinds" option meant.
  const [kinds, setKinds] = React.useState<string[]>([]);
  const [health, setHealth] = React.useState<string[]>([]);
  const [statusSeg, setStatusSeg] = React.useState<StatusSeg>("open");
  const [grouping, setGrouping] = React.useState<"kind" | "none">("kind");
  const [sort, setSort] = React.useState<"attention" | "title" | "newest">(
    "attention",
  );
  // List vs Board — the SAME view-segment the workflow-detail prototype's Goals
  // display menu carries (Matt, 2026-07-23: "the display section on goals is not
  // the same as the prototype and it should be"). Board lays the kind sections
  // out as columns; List stacks them.
  const [view, setView] = React.useState<"list" | "board">("list");

  // Lifecycle filter first — the kind counts must describe what's on screen.
  const afterLifecycle = goals.filter((g) => inStatusSeg(g, statusSeg));
  const hiddenCount = goals.length - afterLifecycle.length;
  const visible = afterLifecycle.filter(
    (g) =>
      (kinds.length === 0 || kinds.includes(g.kind)) &&
      (health.length === 0 || health.includes(goalHealth(g))),
  );
  const strategic = visible.filter((g) => g.kind === "strategic");
  const agentic = visible.filter((g) => g.kind === "agentic");

  // Attention order: unhealthy floats up (band risk→watch→ok for strategic, goal
  // health otherwise) — cheap, and it puts what needs you at the top.
  const bandOrder: Record<GoalBand, number> = { risk: 0, watch: 1, pending: 2, ok: 3 };
  const attentionRank = (g: GoalLedgerItem) =>
    g.kind === "strategic" && g.band != null && g.band !== "pending"
      ? bandOrder[g.band]
      : HEALTH_ORDER.indexOf(goalHealth(g));
  const order = (rows: GoalLedgerItem[]) =>
    [...rows].sort((a, b) =>
      sort === "title"
        ? a.title.localeCompare(b.title)
        : sort === "newest"
          ? b.id - a.id
          : attentionRank(a) - attentionRank(b),
    );

  const sortedStrategic = order(strategic);
  const sortedAgentic = order(agentic);
  const showStrategic = kinds.length === 0 || kinds.includes("strategic");
  const showAgentic = kinds.length === 0 || kinds.includes("agentic");

  // Every control on this page, as data. The funnel answers "which goals?"; the
  // sliders answer "how are they laid out?" — the same two menus Issues carries.
  const filterFields: FilterField[] = [
    {
      kind: "multi",
      id: "kind",
      label: "Kind",
      icon: Target,
      options: [
        { value: "strategic", label: "Strategic", hint: String(strategic.length) },
        { value: "agentic", label: "Agentic", hint: String(agentic.length) },
      ],
      values: kinds,
      onChange: setKinds,
    },
    {
      kind: "multi",
      id: "health",
      label: "Health",
      icon: Activity,
      options: HEALTH_ORDER.map((h) => ({
        value: h,
        label: GOAL_HEALTH_META[h].label,
        hint: String(afterLifecycle.filter((g) => goalHealth(g) === h).length),
      })),
      values: health,
      onChange: setHealth,
    },
    {
      kind: "single",
      id: "lifecycle",
      label: "Lifecycle",
      icon: CircleDashed,
      startsGroup: true,
      // Open is a real default, not "no filter", so this field never offers a
      // clear row — clearing it would mean "no lifecycle", which isn't a state.
      clearable: false,
      options: (["open", "settled", "all"] as StatusSeg[]).map((key) => ({
        value: key,
        label: STATUS_LABEL[key],
      })),
      value: statusSeg,
      onChange: (v) => setStatusSeg((v as StatusSeg) ?? "open"),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* The whole control cluster rides the page TITLE row (Matt, 2026-07-20:
          "All Strategic and Agentic need to be inline with the goals button") —
          the funnel · the sliders · the create CTA, in the shared <SectionActions>
          grammar. Under a ModeShell it portals up beside the serif "Goals";
          standalone it renders inline here. The two menus are the SAME blocks
          Issues carries (packages/ui/src/blocks/view-menu.tsx) — one filter
          vocabulary and one display panel across every collection. */}
      <ShellActionsPortal>
        {/* The create CTA is a SERVER-rendered element passed through props; it
            gets its own wrapper so it never lands bare in a multi-child array
            (React dev-warns about the missing key on a serialized child). */}
        <SectionActions
          primary={
            toolbar ? <div className="flex items-center">{toolbar}</div> : null
          }
        >
          <FilterMenu
            fields={filterFields}
            onReset={() => {
              setKinds([]);
              setHealth([]);
              setStatusSeg("open");
            }}
          />
          <DisplayMenu
            views={{
              options: [
                { value: "list", label: "List", icon: List },
                { value: "board", label: "Board", icon: Kanban },
              ],
              value: view,
              onChange: (v) => setView(v as "list" | "board"),
            }}
            sections={[
              {
                id: "organize",
                controls: [
                  {
                    kind: "select",
                    id: "grouping",
                    label: "Grouping",
                    options: [
                      { value: "kind", label: "Kind" },
                      { value: "none", label: "No grouping" },
                    ],
                    value: grouping,
                    onChange: (v) => setGrouping(v as "kind" | "none"),
                  },
                  {
                    kind: "select",
                    id: "sort",
                    label: "Ordering",
                    options: [
                      { value: "attention", label: "Attention" },
                      { value: "title", label: "Title" },
                      { value: "newest", label: "Newest" },
                    ],
                    value: sort,
                    onChange: (v) =>
                      setSort(v as "attention" | "title" | "newest"),
                  },
                ],
              },
            ]}
          />
        </SectionActions>
      </ShellActionsPortal>

      {/* The applied-filter read-out, directly above the rows it describes —
          the same bar Issues carries, from the same field spec. */}
      <FilterBar
        fields={filterFields}
        onReset={() => {
          setKinds([]);
          setHealth([]);
          setStatusSeg("open");
        }}
        className="rounded-md border border-border/60"
      />

      {statusSeg === "open" && hiddenCount > 0 ? (
        <p className="type-subtext text-muted-foreground">
          {hiddenCount} settled {hiddenCount === 1 ? "goal is" : "goals are"} hidden.
        </p>
      ) : null}

      {/* List stacks the kind sections; Board lays them out side-by-side as
          columns — the List/Board view-segment the workflow-detail prototype's
          Goals display menu carries. */}
      <div
        className={cn(
          view === "board"
            ? "grid items-start gap-3 lg:grid-cols-2"
            : "flex flex-col gap-3",
        )}
      >
      {/* "No grouping" collapses the two kind sections into ONE list — the same
          rows, the same order, without the kind spine. What you want when the
          question is "what needs me?" rather than "how is each half doing?" */}
      {grouping === "none" ? (
        <LedgerSection
          kindLabel="Goals"
          subtitle="Every goal, ungrouped"
          count={visible.length}
          canCreate={false}
          createKind="agentic"
        >
          {visible.length === 0 ? (
            <div className="px-4 py-6 type-body text-muted-foreground">
              No goals match these filters.
            </div>
          ) : (
            <div className="flex flex-col">
              {order(visible).map((g) => (
                <GoalLedgerRow key={g.id} item={g} href={`${basePath}/goals/${g.id}`} />
              ))}
            </div>
          )}
        </LedgerSection>
      ) : null}

      {grouping === "kind" && showStrategic ? (
        <LedgerSection
          kindLabel="Strategic goals"
          subtitle="Human-owned outcomes · measured by key results"
          count={strategic.length}
          canCreate={toolbar != null}
          createKind="strategic"
        >
          {sortedStrategic.length === 0 ? (
            <div className="px-4 py-6 type-body text-muted-foreground">
              {statusSeg === "open"
                ? "No open strategic goals — set the 1–3 outcomes you’re steering toward."
                : statusSeg === "settled"
                  ? "No settled strategic goals yet."
                  : "No strategic goals yet — set the 1–3 outcomes you’re steering toward."}
            </div>
          ) : (
            <div className="flex flex-col">
              {sortedStrategic.map((g) => (
                <GoalLedgerRow key={g.id} item={g} href={`${basePath}/goals/${g.id}`} />
              ))}
            </div>
          )}
        </LedgerSection>
      ) : null}

      {grouping === "kind" && showAgentic ? (
        <LedgerSection
          kindLabel="Agentic goals"
          subtitle="Fleet-pursued · measured by linked issues"
          count={agentic.length}
          canCreate={toolbar != null}
          createKind="agentic"
        >
          {agentic.length === 0 ? (
            <div className="px-4 py-6 type-body text-muted-foreground">
              {statusSeg === "open"
                ? "No open agentic goals — create one to steer fleet work by result."
                : statusSeg === "settled"
                  ? "No settled agentic goals yet."
                  : "No agentic goals yet — create one to steer fleet work by result."}
            </div>
          ) : (
            <div className="flex flex-col">
              {sortedAgentic.map((g) => (
                <GoalLedgerRow key={g.id} item={g} href={`${basePath}/goals/${g.id}`} />
              ))}
            </div>
          )}
        </LedgerSection>
      ) : null}
      </div>
    </div>
  );
}

// ── Flat mode (agentic-only callers) — the original health-filter list ────────

type HealthSeg = "all" | GoalHealth;

const SEGMENT_KEYS: HealthSeg[] = [
  "all",
  "needs-plan",
  "ready",
  "needs-review",
  "in-progress",
  "settled",
];

function segmentMeta(key: HealthSeg): { label: string; intent: Intent } {
  if (key === "all") return { label: "All", intent: "neutral" };
  const m = GOAL_HEALTH_META[key];
  return { label: m.label, intent: m.intent };
}

/** Attention priority — what needs you floats up within "All". */
const HEALTH_ORDER: GoalHealth[] = [
  "ready",
  "needs-plan",
  "needs-review",
  "in-progress",
  "settled",
];

function FlatLedger({ goals, basePath, toolbar }: GoalsLedgerProps) {
  const [segment, setSegment] = React.useState<HealthSeg>("all");

  const withHealth = React.useMemo(
    () => goals.map((g) => ({ g, health: goalHealth(g) })),
    [goals],
  );

  const count = (seg: HealthSeg) =>
    seg === "all"
      ? withHealth.filter((x) => x.g.status === "active").length
      : seg === "settled"
        ? withHealth.filter((x) => x.health === "settled").length
        : withHealth.filter((x) => x.health === seg).length;

  const rows = withHealth
    .filter((x) =>
      segment === "all"
        ? x.g.status === "active"
        : segment === "settled"
          ? x.health === "settled"
          : x.health === segment,
    )
    .sort((a, b) => HEALTH_ORDER.indexOf(a.health) - HEALTH_ORDER.indexOf(b.health));

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="px-0">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
          <div
            role="tablist"
            aria-label="Filter by health"
            className="-mx-1 flex items-center gap-1 overflow-x-auto px-1"
          >
            {SEGMENT_KEYS.map((key) => {
              const on = segment === key;
              const meta = segmentMeta(key);
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setSegment(key)}
                  className={cn(
                    "inline-flex h-7 shrink-0 items-center gap-2 rounded-md px-2.5 type-body transition-colors",
                    on
                      ? "bg-surface-3 text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn("size-2 rounded-full", INTENT_CLASSES[meta.intent].dot)}
                  />
                  {meta.label}
                  <span className="type-subtext tabular-nums text-muted-foreground">{count(key)}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Filter" title="Filter" disabled>
              <Filter className="size-3.5" />
            </Button>
            {toolbar ? (
              <>
                <div className="mx-1 h-5 w-px bg-border" aria-hidden />
                {toolbar}
              </>
            ) : null}
          </div>
        </header>

        {rows.length === 0 ? (
          <EmptyState
            variant="inline"
            className="flex min-h-52 items-center justify-center"
            icon={<Target className="size-6" />}
            title="No goals in this view"
            description="Goals capture the outcomes you're driving toward. Create one to start steering work by result."
          />
        ) : (
          <div className="flex flex-col">
            {rows.map(({ g }) => (
              <GoalLedgerRow key={g.id} item={g} href={`${basePath}/goals/${g.id}`} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Entry — pick the mode from the data ───────────────────────────────────────

export function GoalsLedger(props: GoalsLedgerProps) {
  const hasStrategic = props.goals.some((g) => g.kind === "strategic");
  return hasStrategic ? <ConsolidatedLedger {...props} /> : <FlatLedger {...props} />;
}
